import React from 'react';
import clsx from 'clsx';
import { RiFileUploadLine } from 'react-icons/ri';
import { colors } from 'lib/colors';

interface FileUploadAreaProps {
  onFileSelect: (files: File[]) => void;
  isDragActive?: boolean;
  isUploading?: boolean;
  acceptedFileTypes?: string;
  maxFileSize?: string;
  className?: string;
  children?: React.ReactNode;
}

export const FileUploadArea: React.FC<FileUploadAreaProps> = ({
  onFileSelect,
  isDragActive = false,
  isUploading = false,
  acceptedFileTypes = '.pdf,.doc,.docx,.xlsx,.xls,.jpg,.jpeg,.png,.gif,.webp',
  maxFileSize = '10 MB',
  className,
  children,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleClick = (): void => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files) {
      onFileSelect(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    if (!isUploading && e.dataTransfer.files) {
      onFileSelect(Array.from(e.dataTransfer.files));
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
  };

  return (
    <div
      className={clsx(
        'w-full mb-2 p-4 rounded-md border border-primary-active border-dashed outline-none flex flex-col items-center gap-3 cursor-pointer transition-colors hover:bg-primary/5',
        {
          'border-primary bg-primary/10': isDragActive,
          'opacity-50 cursor-not-allowed': isUploading,
          'hover:bg-primary/5': !isUploading,
        },
        className
      )}
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      role="button"
      tabIndex={0}
      aria-label="Click to upload files or drag and drop"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary/10">
        <RiFileUploadLine color={colors.primary} size={24} />
      </div>

      <div className="flex flex-col items-center gap-1 w-full">
        <div className="flex flex-row gap-1 items-center justify-center w-full">
          <span className="text-tertiary-dark font-bold">
            {isUploading ? 'Uploading...' : 'Click to Upload'}
          </span>
          {!isUploading && <span className="text-body">or drag and drop</span>}
        </div>
        <div className="text-xs text-body opacity-60 text-center w-full">
          (Max. File size: {maxFileSize})
        </div>
      </div>

      {children}

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        accept={acceptedFileTypes}
        onChange={handleFileChange}
        disabled={isUploading}
        aria-hidden="true"
      />
    </div>
  );
};
